import React, { useState, useMemo, useContext, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import StarekContext from "../../context/StarekContext";
import { Button, Spinner as CommonSpinner } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import Spinner from "../../components/Spinner";
import { useNavigate } from "react-router-dom";
import { gql, useQuery, useMutation } from "@apollo/client";
import FavReportsCardView from "./FavReportsCardView";
import FavReportsListingView from "./FavReportsListingView";
import ConfirmModal from "../../components/modals/ConfirmModal";
import "../../starek.css";

const GET_FAVORITE_REPORTS = gql`
    query Query($where: FavoriteReportsWhereInput) {
        findManyFavoriteReports(where: $where) {
            id
            name
            parameters
            description
        }
    }
`;

const DELETE_FAVORITE_REPORT = gql`
    mutation Mutation($where: FavoriteReportsWhereUniqueInput!) {
        deleteOneFavoriteReports(where: $where) {
            id
        }
    }
`;

// UI Strings
const UI_STRINGS = {
    PAGE_TITLE: "Favorite Reports",
    SEARCH_PLACEHOLDER: "Search favourite reports...",
    BUTTON_DELETE: (count) => `Delete ${count} Report${count !== 1 ? "s" : ""}`,
    BUTTON_DELETE_LOADING: (done, total) => `Deleting ${done}/${total}`,
    MODAL_TITLE: (name) =>
        name ? `Delete Report ${name}?` : "Delete Reports?",
};

// Confirmation messages
const REPORT_MESSAGES = {
    DELETE_REPORTS_BULK: (count) => `You are about to delete ${count} report(s).

This action cannot be undone.

Are you sure you wish to continue?`,
};

const FavReports = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [filterText, setFilterText] = useState("");
    const [viewMode, setViewMode] = useState("card");
    const [selectedRows, setSelectedRows] = useState([]);

    useEffect(() => {
        const viewParam = searchParams.get("view");
        if (viewParam && (viewParam === "card" || viewParam === "list")) {
            setViewMode(viewParam);
        }
    }, [searchParams]);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteDone, setDeleteDone] = useState(0);
    const [confirmCfg, setConfirmCfg] = useState({ open: false });
    const [confirmLoading, setConfirmLoading] = useState(false);

    const { user, lastUsedPlant, showError } = useContext(StarekContext);

    const {
        data: reportsData,
        loading,
        refetch,
    } = useQuery(GET_FAVORITE_REPORTS, {
        variables: {
            where: {
                plant_id: {
                    equals: parseInt(lastUsedPlant?.id),
                },
                user_id: {
                    equals: parseInt(user?.id),
                },
            },
        },
        skip: !user?.id,
        fetchPolicy: "network-only",
    });

    const [deleteFavoriteReport] = useMutation(DELETE_FAVORITE_REPORT);

    const reports = reportsData?.findManyFavoriteReports || [];
    const isSingle = selectedRows.length === 1;
    const isAnySelected = selectedRows.length > 0;

    const openConfirm = (cfg) => setConfirmCfg({ open: true, ...cfg });
    const closeConfirm = () => setConfirmCfg({ open: false });

    const filteredItems = useMemo(() => {
        const q = filterText.trim().toLowerCase();
        if (!q) return reports;
        return reports.filter(
            (item) =>
                item.name?.toLowerCase().includes(q) ||
                item.description?.toLowerCase().includes(q)
        );
    }, [filterText, reports]);

    const handleSelectedRowsChange = ({ selectedRows }) => {
        setSelectedRows(selectedRows);
    };

    const handleRunReport = (row) => {
        if (!row.parameters) return;

        let params = row.parameters;
        if (typeof params === "string") {
            try {
                params = JSON.parse(params);
            } catch (e) {
                console.error("Failed to parse report parameters", e);
                return;
            }
        }

        const { filters, search_params } = params;

        if (filters) {
            localStorage.setItem(
                "starek_report_filters",
                JSON.stringify(filters)
            );
        }
        if (search_params && search_params.range) {
            localStorage.setItem(
                "starek_report_range",
                JSON.stringify(search_params.range)
            );
        }

        navigate("/reports");
    };

    const handleBulkDelete = () => {
        if (!isAnySelected) return;
        const itemName = isSingle ? selectedRows[0].name : null;
        openConfirm({
            title: UI_STRINGS.MODAL_TITLE(itemName),
            message: REPORT_MESSAGES.DELETE_REPORTS_BULK(selectedRows.length),
            confirmText: "Delete",
            cancelText: "Cancel",
            type: "danger",
            onConfirm: handleBulkDeleteConfirmed,
            onClose: closeConfirm,
        });
    };

    const handleBulkDeleteConfirmed = async () => {
        setIsDeleting(true);
        setDeleteDone(0);
        setConfirmLoading(true);
        try {
            for (const report of selectedRows) {
                await deleteFavoriteReport({
                    variables: {
                        where: {
                            id: report.id,
                        },
                    },
                });
                setDeleteDone((n) => n + 1);
            }
            setSelectedRows([]);
            await refetch();
        } catch (error) {
            console.error("Error deleting reports", error);
            showError("Failed to delete some reports");
        } finally {
            setIsDeleting(false);
            setDeleteDone(0);
            setConfirmLoading(false);
            closeConfirm();
        }
    };

    return loading ? (
        <div
            className="d-flex flex-column justify-content-center align-items-center"
            style={{ minHeight: "100vh" }}
        >
            <div className="text-xs d-flex justify-content-center">
                <i className="fa fa-star fa-10x text-success"></i>
            </div>
            <Spinner />
        </div>
    ) : (
        <div className="container-fluid">
            {reports && (
                <>
                    <div className="font-weight-bold mb-3">
                        {UI_STRINGS.PAGE_TITLE}
                    </div>

                    <div className="d-flex align-items-center justify-content-between mb-3">
                        <input
                            type="text"
                            className="border rounded p-2"
                            style={{ width: "60%" }}
                            placeholder={UI_STRINGS.SEARCH_PLACEHOLDER}
                            value={filterText}
                            onChange={(e) => setFilterText(e.target.value)}
                        />

                        <div className="d-flex align-items-center gap-2">
                            {/* View Mode Buttons */}
                            <div className="mr-4">
                                <Button
                                    onClick={() => {
                                        setViewMode("card");
                                        setSearchParams({ view: "card" });
                                        setSelectedRows([]);
                                    }}
                                    className={`d-inline-flex align-items-center tw-h-10 border border-info ${
                                        viewMode === "card"
                                            ? "bg-info text-white"
                                            : "bg-white text-info"
                                    }`}
                                >
                                    <i className="fa fa-th" />
                                </Button>
                                <Button
                                    onClick={() => {
                                        setViewMode("list");
                                        setSearchParams({ view: "list" });
                                        setSelectedRows([]);
                                    }}
                                    className={`d-inline-flex align-items-center ms-2 tw-h-10 border border-info ${
                                        viewMode === "list"
                                            ? "bg-info text-white"
                                            : "bg-white text-info"
                                    }`}
                                >
                                    <i className="fa fa-list" />
                                </Button>
                            </div>

                            {/* Delete Button */}
                            {isAnySelected && (
                                <Button
                                    className="me-2"
                                    variant="danger"
                                    onClick={handleBulkDelete}
                                    disabled={confirmLoading || !isAnySelected}
                                    aria-live="polite"
                                >
                                    {confirmLoading ? (
                                        <>
                                            <CommonSpinner
                                                animation="border"
                                                size="sm"
                                                role="status"
                                                aria-hidden="true"
                                                className="me-2"
                                            />
                                            {UI_STRINGS.BUTTON_DELETE_LOADING(
                                                deleteDone,
                                                selectedRows.length
                                            )}
                                        </>
                                    ) : (
                                        UI_STRINGS.BUTTON_DELETE(
                                            selectedRows.length
                                        )
                                    )}
                                </Button>
                            )}
                        </div>
                    </div>

                    {/* View Rendering */}
                    {viewMode === "card" ? (
                        <FavReportsCardView
                            items={reports}
                            selectedRows={selectedRows}
                            onSelectedRowsChange={({ selectedRows }) =>
                                setSelectedRows(selectedRows)
                            }
                            filterText={filterText}
                            onRun={handleRunReport}
                            isDeleting={confirmLoading}
                        />
                    ) : (
                        <FavReportsListingView
                            items={reports}
                            selectedRows={selectedRows}
                            onSelectedRowsChange={({ selectedRows }) =>
                                setSelectedRows(selectedRows)
                            }
                            filterText={filterText}
                            onRun={handleRunReport}
                            isDeleting={confirmLoading}
                        />
                    )}
                </>
            )}

            <ConfirmModal
                open={confirmCfg.open}
                title={confirmCfg.title}
                message={confirmCfg.message}
                confirmText={confirmCfg.confirmText}
                cancelText={confirmCfg.cancelText}
                type={confirmCfg.type}
                loading={confirmLoading}
                onConfirm={confirmCfg.onConfirm}
                onClose={confirmCfg.onClose}
            />
        </div>
    );
};

export default FavReports;
